import { useState, type ReactNode } from 'react';
import { ApiFailure } from './api.ts';

/**
 * THE TYPED-PHRASE ARMING SWITCH, shared by every permanent delete bigger
 * than one recording. Same contract the recording purge established: what
 * guards the act is INTENT, proven by typing the exact name (or phrase)
 * back — no UI can skip it because the server checks it again.
 */
export function ConfirmDanger({
  title,
  warning,
  phrase,
  actionLabel,
  busyLabel,
  onRun,
  onDone,
}: {
  title: string;
  /** The plain sentence saying exactly what is about to go, for ever. */
  warning: ReactNode;
  /** What must be typed, exactly, to arm the button. */
  phrase: string;
  actionLabel: string;
  busyLabel: string;
  /** Performs the delete. Throws to show the failure in place. */
  onRun: () => Promise<void>;
  onDone: (done: boolean) => void;
}): JSX.Element {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const match = typed === phrase;

  const run = async (): Promise<void> => {
    if (!match || busy) return;
    setBusy(true);
    setFailed(null);
    try {
      await onRun();
      onDone(true);
    } catch (err) {
      setFailed(err instanceof ApiFailure ? err.message : 'Could not do it. Try again.');
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onClick={() => !busy && onDone(false)}>
      <div
        className="modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-danger-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="confirm-danger-title">{title}</h2>
        <p className="hint">{warning}</p>
        <p className="hint">
          Type <strong>{phrase}</strong> to arm the button:
        </p>
        <input
          className="input"
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={phrase}
          disabled={busy}
          autoFocus
        />
        {failed && (
          <div className="notice notice-warn" role="status">
            {failed}
          </div>
        )}
        <div className="btn-row">
          <button className="btn-quiet" type="button" disabled={busy} onClick={() => onDone(false)}>
            Cancel
          </button>
          <button
            className="btn-danger"
            type="button"
            style={{ width: 'auto' }}
            disabled={!match || busy}
            onClick={() => void run()}
          >
            {busy ? busyLabel : actionLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
